import React, { FC, useState } from 'react';
import {
  Menu,
  MenuItem,
  IconButton,
  Popover,
  ListItemText,
  Box,
  ClickAwayListener,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { KebabMenuProps, SubMenuOption } from '../../../types/ui';

const KebabMenu: FC<KebabMenuProps> = ({
  options,
  icon,
  sx,
  disable = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [subMenuAnchorEls, setSubMenuAnchorEls] = useState<
    (null | HTMLElement)[]
  >([]);
  const [subMenuOptionsList, setSubMenuOptionsList] = useState<
    SubMenuOption[][]
  >([]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: any) => {
    event.stopPropagation();
    setAnchorEl(null);
    setSubMenuAnchorEls([]);
    setSubMenuOptionsList([]);
  };

  const handleOpenSubMenu = (
    event: React.MouseEvent<HTMLElement>,
    subOptions: SubMenuOption[],
    level: number
  ) => {
    event.stopPropagation(); // Prevent the click from closing the parent menu
    const newAnchorEls = [...subMenuAnchorEls];
    newAnchorEls[level] = event.currentTarget;
    setSubMenuAnchorEls(newAnchorEls);

    const newSubMenuOptionsList = [...subMenuOptionsList];
    newSubMenuOptionsList[level] = subOptions;
    setSubMenuOptionsList(newSubMenuOptionsList);
  };

  const handleCloseSubMenu = (level: number) => {
    const newAnchorEls = subMenuAnchorEls.slice(0, level);
    setSubMenuAnchorEls(newAnchorEls);

    const newSubMenuOptionsList = subMenuOptionsList.slice(0, level);
    setSubMenuOptionsList(newSubMenuOptionsList);
  };

  const renderMenuOptions = (options: SubMenuOption[], level = 0) =>
    options.map((option, index) => (
      <MenuItem
        key={index}
        onClick={(event) => {
          event.stopPropagation();
          if (!option?.subOptions) {
            if (option?.onClick) option?.onClick();
            handleClose(event);
          }
        }}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {option?.icon && (
          <Box component='span' sx={{ marginRight: 1 }}>
            {option?.icon}
          </Box>
        )}
        {option?.prefixIcon && (
          <Box component='span' sx={{ marginRight: 1 }}>
            {option?.prefixIcon}
          </Box>
        )}
        {typeof option?.label === 'string' ? (
          <ListItemText primary={option?.label.toLanguagePack()} />
        ) : (
          <Box component='span'>{option?.label}</Box>
        )}
        {option?.subOptions && (
          <ArrowRightIcon
            onClick={(event: any) =>
              handleOpenSubMenu(event, option.subOptions!, level + 1)
            }
            sx={{ marginLeft: 'auto', cursor: 'pointer', padding: 0 }}
          />
        )}
      </MenuItem>
    ));

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        event.stopPropagation();
        // Check if the click happened inside the menus or submenus
        if (
          anchorEl &&
          !anchorEl.contains(event.target as Node) &&
          !subMenuAnchorEls.some(
            (subMenuAnchorEl) =>
              subMenuAnchorEl && subMenuAnchorEl.contains(event.target as Node)
          )
        ) {
          handleClose(event);
        }
      }}
    >
      <Box sx={sx}>
        <IconButton onClick={handleOpen} disabled={disable} sx={{ padding: 0 }}>
          {icon || <MoreVertIcon />}
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {renderMenuOptions(options)}
        </Menu>
        {subMenuAnchorEls.map((subMenuAnchorEl, index) => (
          <Popover
            key={index}
            anchorEl={subMenuAnchorEl}
            open={Boolean(subMenuAnchorEl)}
            onClose={() => handleCloseSubMenu(index + 1)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: { padding: 0, boxShadow: 'none' },
            }}
          >
            {Boolean(subMenuAnchorEl) && (
              <Menu
                anchorEl={subMenuAnchorEl}
                open={Boolean(subMenuAnchorEl)}
                onClose={() => handleCloseSubMenu(index + 1)}
                MenuListProps={{
                  sx: { padding: 0 },
                }}
              >
                {renderMenuOptions(subMenuOptionsList[index] || [], index + 1)}
              </Menu>
            )}
          </Popover>
        ))}
      </Box>
    </ClickAwayListener>
  );
};

export default KebabMenu;
